import React from "react";

const Mean3CheckoutBoostPrivacyPolicy = () => {
  return (
    <div className="container mx-auto px-4 py-40">
      <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>

      <p className="mb-2">
        <strong>Effective Date:</strong> August 19, 2026
      </p>

      <p className="mb-6">
        <strong>Last Updated:</strong> August 19, 2026
      </p>

      <p className="mb-6">
        Mean3 Checkout Boost ("Mean3 Checkout Boost", "the App", "we", "us",
        or "our") respects the privacy of Shopify merchants and their
        customers. This Privacy Policy explains how information is collected,
        used, processed, stored, shared, and deleted when a Shopify merchant
        installs or uses Mean3 Checkout Boost.
      </p>

      <p className="mb-6">
        Mean3 Checkout Boost is designed to help Shopify merchants enhance
        their checkout experience with features including product
        recommendations, free-shipping progress messages, customer review
        blocks, and trust badges.
      </p>

      <p className="mb-6">
        By installing or using the App, you acknowledge the practices
        described in this Privacy Policy.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        1. Scope of This Privacy Policy
      </h2>

      <p className="mb-2">
        This Privacy Policy applies to information processed through:
      </p>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>The Mean3 Checkout Boost Shopify application.</li>
        <li>Mean3 Checkout Boost checkout extensions.</li>
        <li>Our merchant administration interface.</li>
        <li>Communications between merchants and our support team.</li>
        <li>Shopify APIs and webhooks used to provide the App.</li>
      </ul>

      <p className="mb-6">
        This policy does not govern Shopify's own collection or processing of
        information. Shopify merchants and customers should review Shopify's
        privacy practices separately.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        2. Information We Collect
      </h2>

      <p className="mb-6">
        We collect only the information reasonably necessary to operate,
        maintain, secure, and improve Mean3 Checkout Boost.
      </p>

      <h3 className="text-xl font-semibold mb-2">
        2.1 Shopify Store Information
      </h3>

      <p className="mb-2">
        When a merchant installs the App, Shopify may provide us with
        information relating to the merchant's store, including:
      </p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Shopify shop ID.</li>
        <li>Store domain.</li>
        <li>Store name.</li>
        <li>Store currency.</li>
        <li>Country or market information.</li>
        <li>Shopify plan or installation information where available.</li>
        <li>App permissions and authorized scopes.</li>
        <li>Installation and authorization information.</li>
      </ul>

      <p className="mb-6">
        We use this information to authenticate the store, maintain the App
        installation, apply merchant settings, and provide the App's
        functionality.
      </p>

      <h3 className="text-xl font-semibold mb-2">
        2.2 App Configuration Information
      </h3>

      <p className="mb-2">
        We store information entered or configured by merchants in Mean3
        Checkout Boost, including:
      </p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Enabled or disabled checkout modules.</li>
        <li>Product recommendation mappings.</li>
        <li>Selected products and collections.</li>
        <li>Free-shipping thresholds.</li>
        <li>Shipping progress messages.</li>
        <li>Trust badge titles and descriptions.</li>
        <li>Trust badge configuration.</li>
        <li>Review content selected or entered by the merchant.</li>
        <li>Review display names.</li>
        <li>Ratings entered or selected by the merchant.</li>
        <li>Checkout display and placement settings.</li>
        <li>Visibility and targeting settings.</li>
        <li>Other App preferences.</li>
      </ul>

      <p className="mb-6">
        This information is necessary to display the merchant's configured
        checkout experience.
      </p>

      <h3 className="text-xl font-semibold mb-2">
        2.3 Product and Cart Information
      </h3>

      <p className="mb-2">
        To provide features such as Complete the Look and the Free Shipping
        Bar, the App may process Shopify commerce information such as:
      </p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Product IDs.</li>
        <li>Product titles.</li>
        <li>Product images.</li>
        <li>Variant IDs.</li>
        <li>Variant information.</li>
        <li>Product availability.</li>
        <li>Product prices.</li>
        <li>Compare-at prices where applicable.</li>
        <li>Cart line items.</li>
        <li>Cart quantities.</li>
        <li>Cart subtotal or qualifying cart value.</li>
        <li>Currency.</li>
        <li>Discounts relevant to App calculations.</li>
        <li>
          Market or country context where required for merchant-configured
          rules.
        </li>
      </ul>

      <p className="mb-6">
        This information is used only as necessary to provide the features
        selected by the merchant.
      </p>

      <h3 className="text-xl font-semibold mb-2">
        2.4 Checkout Information
      </h3>

      <p className="mb-2">
        The App may process limited checkout context made available through
        Shopify's supported checkout extension APIs. For example, checkout
        context may be used to:
      </p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Determine which products are currently in the checkout.</li>
        <li>Prevent duplicate product recommendations.</li>
        <li>
          Calculate progress toward the merchant's free-shipping threshold.
        </li>
        <li>Determine whether a configured block should be displayed.</li>
        <li>Add a product selected by the customer to the checkout.</li>
        <li>Display appropriate merchant-configured content.</li>
      </ul>

      <p className="mb-6">
        Mean3 Checkout Boost does not independently process payments.
      </p>

      <h3 className="text-xl font-semibold mb-2">
        2.5 Customer Information
      </h3>

      <p className="mb-2">
        The current App is designed to minimize the collection and storage of
        personally identifiable customer information.
      </p>

      <p className="mb-2">
        The core features of Complete the Look, Free Shipping Bar, and Trust
        Badges do not require us to store a customer's:
      </p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Full name.</li>
        <li>Email address.</li>
        <li>Telephone number.</li>
        <li>Billing address.</li>
        <li>Shipping address.</li>
        <li>Payment card number.</li>
        <li>Card security code.</li>
        <li>Shopify account password.</li>
      </ul>

      <p className="mb-4">
        Where a merchant enters customer review content into the App, that
        information may include a customer display name, initials, review
        text, rating, or other information supplied by the merchant.
      </p>

      <p className="mb-4">
        Merchants are responsible for ensuring that they have appropriate
        rights or authorization to display review content submitted through
        the App.
      </p>

      <p className="mb-6">
        If future features require additional protected customer data, we
        will update our data practices, Shopify permissions, and this Privacy
        Policy as appropriate before using such information.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        3. Information We Do Not Collect
      </h2>

      <p className="mb-2">
        Mean3 Checkout Boost does not intentionally collect or store:
      </p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Full payment card numbers.</li>
        <li>CVV or card security codes.</li>
        <li>Online banking credentials.</li>
        <li>Shopify merchant passwords.</li>
        <li>Customer passwords.</li>
        <li>Government identification numbers.</li>
        <li>Biometric information.</li>
      </ul>

      <p className="mb-6">
        Payment information entered during Shopify checkout is processed by
        Shopify and the merchant's authorized payment providers, not by Mean3
        Checkout Boost.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        4. Technical and Usage Information
      </h2>

      <p className="mb-2">
        When merchants use the App, our systems may automatically generate
        limited technical information necessary for security,
        troubleshooting, and application operation. This information may
        include:
      </p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Request timestamps.</li>
        <li>App events.</li>
        <li>Error messages.</li>
        <li>Browser or device information.</li>
        <li>IP address in server or security logs.</li>
        <li>App version.</li>
        <li>Store identifier.</li>
        <li>Feature interaction information.</li>
        <li>Performance and diagnostic information.</li>
      </ul>

      <p className="mb-4">
        We may use aggregated or de-identified information to understand how
        the App performs and to improve reliability.
      </p>

      <p className="mb-6">
        We do not use checkout information to build advertising profiles or
        provide cross-site behavioral advertising.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        5. How We Use Information
      </h2>

      <p className="mb-4">
        We may use information processed through Mean3 Checkout Boost to:
      </p>

      <h3 className="text-xl font-semibold mb-2">Provide the App</h3>

      <p className="mb-2">We use information to operate features such as:</p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Complete the Look product recommendations.</li>
        <li>Free Shipping Bar calculations.</li>
        <li>Trust Badges.</li>
        <li>Review Blocks.</li>
        <li>Merchant configuration controls.</li>
        <li>Checkout visibility rules.</li>
      </ul>

      <h3 className="text-xl font-semibold mb-2">
        Maintain Merchant Settings
      </h3>

      <p className="mb-6">
        We store merchant settings so that configured checkout blocks
        continue to function correctly.
      </p>

      <h3 className="text-xl font-semibold mb-2">
        Authenticate and Secure the App
      </h3>

      <p className="mb-2">Information may be processed to:</p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Authenticate merchants.</li>
        <li>Maintain authorized Shopify connections.</li>
        <li>Detect unauthorized activity.</li>
        <li>Prevent abuse.</li>
        <li>Investigate security incidents.</li>
        <li>Maintain system integrity.</li>
      </ul>

      <h3 className="text-xl font-semibold mb-2">
        Provide Customer Support
      </h3>

      <p className="mb-6">
        When a merchant contacts us, we may use the information they provide
        to investigate and resolve their request.
      </p>

      <h3 className="text-xl font-semibold mb-2">Improve the App</h3>

      <p className="mb-2">
        We may analyze technical, diagnostic, aggregated, or de-identified
        information to:
      </p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Identify errors.</li>
        <li>Improve performance.</li>
        <li>Improve usability.</li>
        <li>Develop App functionality.</li>
        <li>Monitor service reliability.</li>
      </ul>

      <h3 className="text-xl font-semibold mb-2">
        Meet Legal and Compliance Requirements
      </h3>

      <p className="mb-2">
        We may process information where reasonably necessary to:
      </p>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>Comply with applicable laws.</li>
        <li>Respond to valid legal requests.</li>
        <li>Protect our rights.</li>
        <li>Enforce agreements.</li>
        <li>Meet Shopify platform requirements.</li>
        <li>Respond to privacy and data-subject requests.</li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4">
        6. Legal Bases for Processing
      </h2>

      <p className="mb-4">
        Where applicable privacy laws require a legal basis for processing
        personal information, we may rely on one or more of the following:
      </p>

      <h3 className="text-xl font-semibold mb-2">
        Contractual Necessity
      </h3>

      <p className="mb-6">
        Processing necessary to provide Mean3 Checkout Boost to merchants who
        install or subscribe to the App.
      </p>

      <h3 className="text-xl font-semibold mb-2">
        Legitimate Interests
      </h3>

      <p className="mb-2">
        Processing necessary for legitimate business interests such as:
      </p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Maintaining App security.</li>
        <li>Preventing fraud and abuse.</li>
        <li>Troubleshooting.</li>
        <li>Improving App reliability.</li>
        <li>Providing merchant support.</li>
      </ul>

      <p className="mb-6">
        We consider applicable privacy rights when relying on legitimate
        interests.
      </p>

      <h3 className="text-xl font-semibold mb-2">Legal Obligations</h3>

      <p className="mb-6">
        Processing necessary to comply with laws, regulatory requirements,
        court orders, or other valid legal obligations.
      </p>

      <h3 className="text-xl font-semibold mb-2">Consent</h3>

      <p className="mb-6">
        Where consent is required by applicable law, we will rely on consent
        or on confirmation from the merchant that the necessary consent has
        been obtained.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        7. How We Share Information
      </h2>

      <p className="mb-2">
        We do not sell personal information.
      </p>

      <p className="mb-2">
        We do not rent customer information to third parties.
      </p>

      <p className="mb-4">
        We do not use merchant customer information for independent
        advertising purposes.
      </p>

      <p className="mb-4">
        We may disclose limited information in the following circumstances.
      </p>

      <h3 className="text-xl font-semibold mb-2">Shopify</h3>

      <p className="mb-6">
        Information is processed in connection with Shopify because Mean3
        Checkout Boost operates as a Shopify application.
      </p>

      <h3 className="text-xl font-semibold mb-2">Service Providers</h3>

      <p className="mb-2">
        We may use carefully selected service providers to operate the App,
        including providers of:
      </p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Cloud hosting.</li>
        <li>Database infrastructure.</li>
        <li>Security services.</li>
        <li>Error monitoring.</li>
        <li>Application monitoring.</li>
        <li>Email or customer support.</li>
        <li>Backup infrastructure.</li>
      </ul>

      <p className="mb-6">
        These service providers are permitted to process information only as
        reasonably necessary to provide services to us and are subject to
        appropriate contractual or confidentiality obligations.
      </p>

      <h3 className="text-xl font-semibold mb-2">Legal Requirements</h3>

      <p className="mb-2">
        We may disclose information if we reasonably believe disclosure is
        required to:
      </p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Comply with applicable law.</li>
        <li>Respond to a court order or lawful government request.</li>
        <li>Protect merchants or customers.</li>
        <li>Investigate fraud or security incidents.</li>
        <li>Protect our legal rights.</li>
      </ul>

      <h3 className="text-xl font-semibold mb-2">
        Business Transactions
      </h3>

      <p className="mb-6">
        If our business or the App is involved in a merger, acquisition,
        restructuring, financing, or sale of assets, relevant information may
        be transferred as part of that transaction, subject to applicable
        law.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        8. Data Retention
      </h2>

      <p className="mb-4">
        We retain information only for as long as reasonably necessary for the
        purpose for which it was collected.
      </p>

      <p className="mb-4">
        Merchant configuration information is generally retained while the
        merchant has Mean3 Checkout Boost installed.
      </p>

      <p className="mb-4">
        When a merchant uninstalls the App, personal information associated
        with the store will be deleted or anonymized in accordance with
        applicable requirements and Shopify's valid shop-redaction process,
        unless we are legally required to retain certain information.
      </p>

      <p className="mb-4">
        Valid Shopify privacy deletion requests will generally be completed
        within 30 days of receipt unless applicable law requires or permits us
        to retain specific information.
      </p>

      <p className="mb-2">
        Certain limited information may be retained for longer where necessary
        for:
      </p>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>Legal compliance.</li>
        <li>Financial or accounting requirements.</li>
        <li>Fraud prevention.</li>
        <li>Security investigations.</li>
        <li>Resolving disputes.</li>
        <li>Enforcing agreements.</li>
      </ul>

      <p className="mb-6">
        Where information is retained for these purposes, access will be
        limited appropriately.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        9. Shopify Privacy Requests
      </h2>

      <p className="mb-4">
        Mean3 Checkout Boost supports Shopify's required privacy processes.
        Depending on the request, Shopify may notify us when:
      </p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>A customer requests access to their personal information.</li>
        <li>A customer requests deletion of their personal information.</li>
        <li>
          A merchant uninstalls the App and store-related information must be
          deleted.
        </li>
      </ul>

      <p className="mb-6">
        We will process valid requests in accordance with applicable
        requirements.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        10. Data Security
      </h2>

      <p className="mb-2">
        We use reasonable administrative, organizational, and technical
        safeguards designed to protect information processed through Mean3
        Checkout Boost. Measures may include:
      </p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Secure HTTPS communication.</li>
        <li>Access controls.</li>
        <li>Restricted administrative access.</li>
        <li>Authentication controls.</li>
        <li>Data minimization.</li>
        <li>Secure application development practices.</li>
        <li>Security monitoring.</li>
        <li>Regular software updates.</li>
      </ul>

      <p className="mb-4">
        Only personnel and service providers who reasonably require access to
        information for authorized purposes should receive such access.
      </p>

      <p className="mb-6">
        No online system can guarantee absolute security, and we cannot
        guarantee that information will never be accessed, disclosed, altered,
        or destroyed without authorization.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        11. International Data Transfers
      </h2>

      <p className="mb-6">
        Mean3 Checkout Boost and its service providers may process information
        in countries other than the country where a merchant or customer is
        located. Where required by applicable law, we use appropriate
        safeguards for international transfers of personal information. These
        safeguards may include contractual protections or other legally
        recognized transfer mechanisms.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        12. Merchant Responsibilities
      </h2>

      <p className="mb-2">
        Shopify merchants using Mean3 Checkout Boost remain responsible for
        their own privacy practices and obligations relating to their
        customers.
      </p>

      <p className="mb-2">Merchants are responsible for:</p>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>
          Providing customers with any privacy notices required by applicable
          law.
        </li>
        <li>
          Ensuring review content displayed through the App is legitimate and
          authorized.
        </li>
        <li>Configuring trust badges accurately.</li>
        <li>Avoiding misleading customer claims.</li>
        <li>Obtaining consent where required.</li>
        <li>Responding appropriately to customer privacy requests.</li>
        <li>
          Using Mean3 Checkout Boost in accordance with applicable privacy and
          consumer-protection laws.
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4">
        13. Customer Reviews
      </h2>

      <p className="mb-4">
        Mean3 Checkout Boost allows merchants to display merchant-selected
        review content. The App does not require merchants to fabricate or
        generate customer reviews.
      </p>

      <p className="mb-4">
        Merchants should only publish reviews, ratings, verified-buyer labels,
        customer names, or other review-related information that they have the
        right to use and that accurately represents the underlying customer
        feedback.
      </p>

      <p className="mb-2">
        Review information entered by a merchant may remain stored until:
      </p>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>The merchant deletes it.</li>
        <li>The merchant replaces it.</li>
        <li>The App is uninstalled.</li>
        <li>A valid deletion request requires its removal.</li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4">
        14. Children's Privacy
      </h2>

      <p className="mb-4">
        Mean3 Checkout Boost is a business application intended for Shopify
        merchants and is not directed to children.
      </p>

      <p className="mb-6">
        We do not knowingly use the App to independently collect personal
        information from children for advertising or profiling purposes. If we
        become aware that information relating to a child has been processed
        contrary to applicable law, we will take appropriate steps to address
        the matter.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        15. Privacy Rights
      </h2>

      <p className="mb-2">
        Depending on the location and applicable law, individuals may have
        rights regarding their personal information, including rights to:
      </p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Request access to personal information.</li>
        <li>Request correction of inaccurate information.</li>
        <li>Request deletion.</li>
        <li>Request restriction of certain processing.</li>
        <li>Object to certain processing.</li>
        <li>Obtain a copy of certain information.</li>
        <li>Withdraw consent where processing is based on consent.</li>
        <li>Submit a complaint to an applicable privacy regulator.</li>
      </ul>

      <p className="mb-4">
        Because merchants control their Shopify stores and customer
        relationships, customers should generally submit storefront-related
        privacy requests directly to the Shopify merchant from whom they
        purchased.
      </p>

      <p className="mb-4">
        Merchants may then submit or facilitate the appropriate request
        through Shopify where necessary.
      </p>

      <p className="mb-6">
        Individuals may also contact us using the details provided below.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        16. United States Privacy Disclosures
      </h2>

      <p className="mb-4">
        Where applicable United States state privacy laws provide additional
        rights, eligible individuals may have rights to access, correct, or
        delete personal information and to obtain information regarding its
        processing.
      </p>

      <p className="mb-4">
        Mean3 Checkout Boost does not sell personal information.
      </p>

      <p className="mb-4">
        Mean3 Checkout Boost does not use merchant customer information for
        cross-context behavioral advertising.
      </p>

      <p className="mb-6">
        Where legally required, we will respond to valid privacy requests and
        appeals in accordance with applicable law.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        17. European Economic Area and United Kingdom
      </h2>

      <p className="mb-4">
        Where the General Data Protection Regulation or United Kingdom GDPR
        applies, the role we perform may depend on the context of the
        processing.
      </p>

      <p className="mb-4">
        For information processed on a merchant's behalf in connection with
        providing App functionality, we may act as a processor or service
        provider.
      </p>

      <p className="mb-4">
        For information we process for our own legitimate administrative
        purposes, such as merchant support, security, legal compliance, or
        managing our business relationship, we may act as an independent
        controller.
      </p>

      <p className="mb-6">
        Merchants requiring additional data-processing terms may contact us
        using the information below.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        18. Data Processing Agreements
      </h2>

      <p className="mb-4">
        Where required, we may make appropriate data-processing terms
        available to merchants whose use of the App is subject to applicable
        data-protection legislation.
      </p>

      <p className="mb-6">
        Requests relating to data-processing terms may be submitted through
        our privacy contact information.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        19. Do Not Sell or Share
      </h2>

      <p className="mb-4">
        We do not sell personal information obtained through Mean3 Checkout
        Boost.
      </p>

      <p className="mb-4">
        We do not share merchant customer personal information with third
        parties for cross-context behavioral advertising.
      </p>

      <p className="mb-6">
        Accordingly, we do not use Shopify checkout information to create
        advertising audiences for unrelated businesses.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        20. Changes to This Privacy Policy
      </h2>

      <p className="mb-2">
        We may update this Privacy Policy periodically to reflect:
      </p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Changes to Mean3 Checkout Boost.</li>
        <li>New App functionality.</li>
        <li>Changes to Shopify requirements.</li>
        <li>Changes in our service providers.</li>
        <li>Changes to applicable privacy requirements.</li>
        <li>Changes to our data-processing practices.</li>
      </ul>

      <p className="mb-4">
        When this Privacy Policy is updated, the "Last Updated" date at the
        top of the policy will be revised.
      </p>

      <p className="mb-6">
        Where required by applicable law, we will provide additional notice
        of material changes.
      </p>

      <h2 className="text-2xl font-semibold mb-4">21. Contact Us</h2>

      <p className="mb-4">
        If you have questions about this Privacy Policy, our data practices,
        or a privacy request, please contact us.
      </p>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>
          <strong>App:</strong> Mean3 Checkout Boost
        </li>
        <li>
          <strong>Developer / Legal Entity:</strong> Mean3 (Private) Limited
        </li>
        <li>
          <strong>Privacy Email:</strong>{" "}
          <a
            href="mailto:info@mean3.com"
            className="text-blue-500 hover:underline"
          >
            info@mean3.com
          </a>
        </li>
        <li>
          <strong>Support Email:</strong>{" "}
          <a
            href="mailto:Support@mean3.com"
            className="text-blue-500 hover:underline"
          >
            Support@mean3.com
          </a>
        </li>
        <li>
          <strong>Website:</strong>{" "}
          <a
            href="https://mean3.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:underline"
          >
            https://mean3.com/
          </a>
        </li>
        <li>
          <strong>Business Address:</strong> 447 Broadway, 2nd Floor, Suite
          #670, New York, NY 10013, United States
        </li>
      </ul>

      <p className="mb-6">
        For customer information originating from a Shopify store, customers
        may also contact the relevant merchant directly.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        22. Privacy Request Information
      </h2>

      <p className="mb-4">
        Privacy requests should include sufficient information for us to
        understand and verify the request without requiring unnecessary
        additional personal information.
      </p>

      <p className="mb-2">Please include, where applicable:</p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Shopify store domain.</li>
        <li>Merchant name.</li>
        <li>Nature of the request.</li>
        <li>
          Relevant customer or Shopify request reference where available.
        </li>
      </ul>

      <p className="mb-6">
        Do not send payment card information, passwords, or other sensitive
        credentials when submitting a privacy request.
      </p>

      <p className="font-semibold mt-8">
        End of Privacy Policy
      </p>
    </div>
  );
};

export default Mean3CheckoutBoostPrivacyPolicy;
